import { createParser } from 'nuqs';
import {
  type DateRangePresetId,
  isCustomPreset,
  PRESET_MENU_ORDER,
} from '@app/components/date-range-selector/date-range-presets';

const VALID_PRESET_IDS = new Set<string>(PRESET_MENU_ORDER);

/**
 * URL-serializable value for the date range filter.
 * For non-custom presets only presetId is needed; for custom presets from/to are ISO date strings.
 */
export interface DateRangeFilterValue {
  presetId: DateRangePresetId;
  from?: string;
  to?: string;
}

/**
 * nuqs parser for date range filter. The filter value is the preset.
 * Non-custom presets: plain string in URL (e.g. "last7Days").
 * Custom presets: JSON with presetId and from/to.
 */
export const parseAsDateRangeFilter = createParser<DateRangeFilterValue | null>({
  parse(value) {
    if (!value || typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    // Plain preset id (no date range conversion; preset is the value)
    if (VALID_PRESET_IDS.has(trimmed)) {
      if (isCustomPreset(trimmed as DateRangePresetId)) {
        return null;
      } // custom needs from/to in JSON

      return { presetId: trimmed as DateRangePresetId };
    }

    try {
      const raw = JSON.parse(value) as unknown;
      if (!raw || typeof raw !== 'object') {
        return null;
      }

      const rawObj = raw as Record<string, unknown>;
      if (!('presetId' in rawObj)) {
        return null;
      }

      const presetId = String(rawObj.presetId);
      if (!VALID_PRESET_IDS.has(presetId)) {
        return null;
      }

      const from = typeof rawObj.from === 'string' ? rawObj.from : undefined;
      const to = typeof rawObj.to === 'string' ? rawObj.to : undefined;
      if (isCustomPreset(presetId as DateRangePresetId) && (!from || !to)) {
        return null;
      }

      return { presetId: presetId as DateRangePresetId, from, to };
    } catch {
      return null;
    }
  },
  serialize(value): string {
    if (value == null) {
      return '';
    }

    // Non-custom preset: store only the preset id (preset is the filter value)
    if (!isCustomPreset(value.presetId)) {
      return value.presetId;
    }

    return JSON.stringify({
      presetId: value.presetId,
      ...(value.from != null && { from: value.from }),
      ...(value.to != null && { to: value.to }),
    });
  },
  eq(a, b) {
    if (a === b) {
      return true;
    }

    if (a == null || b == null) {
      return false;
    }

    return (
      a.presetId === b.presetId &&
      (a.from ?? '') === (b.from ?? '') &&
      (a.to ?? '') === (b.to ?? '')
    );
  },
});
